import { LocalDate } from "@js-joda/core";
import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import { CustomIcon } from "components/icon/customIcon.component";
import { SecondaryButton } from "components/designV2/buttons/secondaryButton";
import { DatePicker } from "components/designV2/datePicker/datePicker";
import { LabeledValue } from "components/designV2/labeledValue/labeledValue";
import { SidePanel } from "components/designV2/sidePanel/sidePanel";
import { Tag } from "components/designV2/tag/tag";
import { Text } from "components/designV2/text/text";
import { ColorV2 } from "config/colors";
import type { CompanyBankAccountType } from "model/Company";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import {
  ExpandableSectionBody,
  ExpandableSectionButton,
  ExpandableSectionCard,
  ExpandableSectionChevron,
  ExpandableSectionMain,
  ExpandableSectionSubtitle,
  ExpandableSectionText,
  ExpandableSectionTitle,
  FooterActions,
  InfoCard,
  InfoTitle,
  Section,
  SectionDescription,
  SummaryAction,
  SummaryActionRow,
  StateText,
  SummaryCard,
  SummaryDetails,
  SummaryGrid,
  SummaryLabel,
  SummaryMetaRow,
  SummarySettingsRow,
  SummarySettingsRowLabel,
  SummarySettingsRows,
  SummarySettingsRowValue,
  SummarySettingsLink,
  SummaryValue,
  WorkerAmount,
  WorkerList,
  WorkerName,
  WorkerRow,
} from "./salaryPayoutPanel.styled";
import {
  buildSalaryPayoutSectionsViewModel,
  buildSalaryPayoutPanelViewModel,
  type SalaryPayoutExcludedWorkerRowViewModel,
  type SalaryPayoutIncludedWorkerRowViewModel,
} from "./salaryPayoutPanel.logic";
import type { SalaryPayoutPreviewState } from "./useSalaryPayoutPanel";

interface SalaryPayoutPanelProps {
  bankAgreementId: string;
  bankAccountType: CompanyBankAccountType;
  bankAccountValue: string;
  canCreatePayoutFile: boolean;
  currentDate: LocalDate;
  excludedWorkerCount: number;
  isCreatingPayoutFile: boolean;
  isOpen: boolean;
  payoutDate: LocalDate | null;
  payoutWorkerCount: number;
  previewState: SalaryPayoutPreviewState;
  totalNetPayoutAmount: number;
  onClose: () => void;
  onCreate: () => Promise<void>;
  onPayoutDateChange: (date: LocalDate | null) => void;
}

const renderExcludedWorkerRows = ({
  rowTone = "danger",
  tagBackgroundColor,
  tagTextColor,
  textTone = "default",
  workers,
}: {
  readonly rowTone?: "default" | "danger";
  readonly tagBackgroundColor: ColorV2;
  readonly tagTextColor: ColorV2;
  readonly textTone?: "default" | "danger";
  readonly workers: readonly SalaryPayoutExcludedWorkerRowViewModel[];
}): JSX.Element[] => {
  return workers.map((worker) => {
    return (
      <WorkerRow key={`excluded-worker-${worker.workerId}`} $tone={rowTone}>
        <WorkerName $tone={textTone}>{worker.fullName}</WorkerName>
        <Tag
          text={worker.tagLabel}
          backgroundColor={tagBackgroundColor}
          textColor={tagTextColor}
        />
      </WorkerRow>
    );
  });
};

const renderIncludedWorkerRows = ({
  workers,
}: {
  readonly workers: readonly SalaryPayoutIncludedWorkerRowViewModel[];
}): JSX.Element[] => {
  return workers.map((worker) => {
    return (
      <WorkerRow key={`payout-worker-${worker.workerId}`}>
        <WorkerName>{worker.fullName}</WorkerName>
        <WorkerAmount>{worker.payoutAmount}</WorkerAmount>
      </WorkerRow>
    );
  });
};

export const SalaryPayoutPanel = (
  props: SalaryPayoutPanelProps,
): JSX.Element => {
  const { t } = useTranslation();
  const history = useHistory();
  const [isMissingPaymentMethodOpen, setIsMissingPaymentMethodOpen] =
    useState(false);
  const [isMissingPayoutOpen, setIsMissingPayoutOpen] = useState(false);
  const [isIncludedPayoutOpen, setIsIncludedPayoutOpen] = useState(false);
  const viewModel = useMemo(() => {
    return buildSalaryPayoutPanelViewModel({
      bankAccountType: props.bankAccountType,
      bankAccountValue: props.bankAccountValue,
      bankAgreementId: props.bankAgreementId,
      currentDate: props.currentDate,
      totalNetPayoutAmount: props.totalNetPayoutAmount,
      translate: t,
    });
  }, [
    props.bankAccountType,
    props.bankAccountValue,
    props.bankAgreementId,
    props.currentDate,
    props.totalNetPayoutAmount,
    t,
  ]);
  const sectionsViewModel = useMemo(() => {
    if (props.previewState.status !== "loaded") return null;

    return buildSalaryPayoutSectionsViewModel({
      previewData: props.previewState.data,
      totalNetPayoutAmount: props.totalNetPayoutAmount,
      translate: t,
    });
  }, [props.previewState, props.totalNetPayoutAmount, t]);

  useEffect(() => {
    if (!props.isOpen) {
      setIsMissingPaymentMethodOpen(false);
      setIsMissingPayoutOpen(false);
      setIsIncludedPayoutOpen(false);
    }
  }, [props.isOpen]);

  const handleOpenBankSettings = (): void => {
    props.onClose();
    history.push("/company/settings-v2?page=System&category=bankAccount");
  };

  const footer = (
    <FooterActions>
      <SecondaryButton
        label={t("Salary.Cancel")}
        size="small"
        onClick={props.onClose}
        disabled={props.isCreatingPayoutFile}
      />
      <PrimaryButton
        label={t("Salary.CreatePaymentFile")}
        size="small"
        onClick={() => void props.onCreate()}
        disabled={!props.canCreatePayoutFile}
        isLoading={props.isCreatingPayoutFile}
      />
    </FooterActions>
  );

  const renderState = (): JSX.Element => {
    switch (props.previewState.status) {
      case "idle":
      case "loading":
        return (
          <StateText>
            <Text typography="b2">{t("General.Loading")}</Text>
          </StateText>
        );
      case "error":
        return (
          <StateText>
            <Text typography="b2" color={ColorV2.NegativeRed}>
              {t("Salary.PaymentPreviewLoadError")}
            </Text>
          </StateText>
        );
      case "loaded": {
        if (sectionsViewModel == null) {
          return (
            <StateText>
              <Text typography="b2" color={ColorV2.NegativeRed}>
                {t("Salary.PaymentPreviewLoadError")}
              </Text>
            </StateText>
          );
        }

        const missingPaymentMethodSection =
          sectionsViewModel.excludedSections.find(
            (section) => section.id === "missingPaymentMethod",
          ) ?? null;
        const missingPayoutSection =
          sectionsViewModel.excludedSections.find(
            (section) => section.id === "missingPayout",
          ) ?? null;
        const hasPayoutWorkers =
          sectionsViewModel.includedSection.rows.length > 0;

        return (
          <>
            {props.excludedWorkerCount > 0 && (
              <Section>
                {missingPaymentMethodSection != null && (
                  <ExpandableSectionCard>
                    <ExpandableSectionButton
                      type="button"
                      onClick={() =>
                        setIsMissingPaymentMethodOpen(
                          (currentValue) => !currentValue,
                        )
                      }
                      aria-expanded={isMissingPaymentMethodOpen}
                    >
                      <ExpandableSectionMain>
                        <ExpandableSectionText>
                          <ExpandableSectionTitle $tone="danger">
                            {missingPaymentMethodSection.title}
                          </ExpandableSectionTitle>
                          <ExpandableSectionSubtitle>
                            {missingPaymentMethodSection.subtitle}
                          </ExpandableSectionSubtitle>
                        </ExpandableSectionText>
                      </ExpandableSectionMain>
                      <ExpandableSectionChevron
                        $isOpen={isMissingPaymentMethodOpen}
                      >
                        <CustomIcon
                          name="v2/down-chevron"
                          size="24px"
                          color={ColorV2.Blue4}
                        />
                      </ExpandableSectionChevron>
                    </ExpandableSectionButton>
                    {isMissingPaymentMethodOpen && (
                      <ExpandableSectionBody>
                        <WorkerList>
                          {renderExcludedWorkerRows({
                            workers: missingPaymentMethodSection.rows,
                            tagBackgroundColor: ColorV2.NegativeRedLight,
                            tagTextColor: ColorV2.NegativeRed,
                            rowTone: "default",
                            textTone: "default",
                          })}
                        </WorkerList>
                      </ExpandableSectionBody>
                    )}
                  </ExpandableSectionCard>
                )}
                {missingPayoutSection != null && (
                  <ExpandableSectionCard>
                    <ExpandableSectionButton
                      type="button"
                      onClick={() =>
                        setIsMissingPayoutOpen((currentValue) => !currentValue)
                      }
                      aria-expanded={isMissingPayoutOpen}
                    >
                      <ExpandableSectionMain>
                        <ExpandableSectionText>
                          <ExpandableSectionTitle $tone="danger">
                            {missingPayoutSection.title}
                          </ExpandableSectionTitle>
                          <ExpandableSectionSubtitle>
                            {missingPayoutSection.subtitle}
                          </ExpandableSectionSubtitle>
                        </ExpandableSectionText>
                      </ExpandableSectionMain>
                      <ExpandableSectionChevron $isOpen={isMissingPayoutOpen}>
                        <CustomIcon
                          name="v2/down-chevron"
                          size="24px"
                          color={ColorV2.Blue4}
                        />
                      </ExpandableSectionChevron>
                    </ExpandableSectionButton>
                    {isMissingPayoutOpen && (
                      <ExpandableSectionBody>
                        <WorkerList>
                          {renderExcludedWorkerRows({
                            workers: missingPayoutSection.rows,
                            tagBackgroundColor: ColorV2.NegativeRedLight,
                            tagTextColor: ColorV2.NegativeRed,
                            rowTone: "default",
                            textTone: "default",
                          })}
                        </WorkerList>
                      </ExpandableSectionBody>
                    )}
                  </ExpandableSectionCard>
                )}
              </Section>
            )}

            <Section>
              <ExpandableSectionCard>
                <ExpandableSectionButton
                  type="button"
                  onClick={() =>
                    setIsIncludedPayoutOpen((currentValue) => !currentValue)
                  }
                  aria-expanded={isIncludedPayoutOpen}
                >
                  <ExpandableSectionMain>
                    <ExpandableSectionText>
                      <ExpandableSectionTitle $tone="default">
                        {sectionsViewModel.includedSection.title}
                      </ExpandableSectionTitle>
                      <ExpandableSectionSubtitle>
                        {sectionsViewModel.includedSection.subtitle}
                      </ExpandableSectionSubtitle>
                    </ExpandableSectionText>
                  </ExpandableSectionMain>
                  <ExpandableSectionChevron $isOpen={isIncludedPayoutOpen}>
                    <CustomIcon
                      name="v2/down-chevron"
                      size="24px"
                      color={ColorV2.Blue4}
                    />
                  </ExpandableSectionChevron>
                </ExpandableSectionButton>
                {isIncludedPayoutOpen && (
                  <ExpandableSectionBody>
                    {hasPayoutWorkers ? (
                      <WorkerList>
                        {renderIncludedWorkerRows({
                          workers: sectionsViewModel.includedSection.rows,
                        })}
                      </WorkerList>
                    ) : (
                      <SectionDescription>
                        {sectionsViewModel.includedSection.emptyText}
                      </SectionDescription>
                    )}
                  </ExpandableSectionBody>
                )}
              </ExpandableSectionCard>
            </Section>
          </>
        );
      }
    }
  };

  return (
    <SidePanel
      isOpen={props.isOpen}
      onClose={props.onClose}
      title={t("Salary.CreatePaymentFileTitle")}
      footer={footer}
    >
      <SummaryGrid>
        <SummaryCard>
          <LabeledValue
            value={viewModel.currentMonthLabel}
            label={t("Salary.PaymentRun")}
          />
        </SummaryCard>
        <SummaryCard>
          <SummaryActionRow>
            <SummaryDetails>
              <SummaryValue>{props.payoutDate?.toString() ?? ""}</SummaryValue>
              <SummaryLabel>{t("Salary.PaymentPayoutDate")}</SummaryLabel>
            </SummaryDetails>
            <SummaryAction>
              <DatePicker
                value={props.payoutDate}
                onChange={props.onPayoutDateChange}
                dateFormat="YYYY-MM-DD"
                trigger="button"
                triggerLabel={t("Salary.PaymentChangeAction")}
                size="compact"
                menuAlignment="right"
              />
            </SummaryAction>
          </SummaryActionRow>
        </SummaryCard>
        <SummaryCard>
          <LabeledValue
            value={t("Salary.PaymentRecipientsCount", {
              count: props.payoutWorkerCount,
            })}
            label={t("Salary.PaymentRecipients")}
          />
        </SummaryCard>
        <SummaryCard>
          <LabeledValue
            value={viewModel.totalNetPayoutAmount}
            label={t("Salary.PaymentTotalNetAmount")}
          />
        </SummaryCard>
        <SummaryCard $fullWidth>
          <SummarySettingsRows>
            <SummarySettingsRow>
              <SummarySettingsRowLabel>
                {t("System.CompanyBankAccountValue")}
              </SummarySettingsRowLabel>
              <SummarySettingsRowValue>
                {viewModel.bankAccountValue}
              </SummarySettingsRowValue>
            </SummarySettingsRow>
            <SummarySettingsRow>
              <SummarySettingsRowLabel>
                {t("Salary.PaymentFileFormatLabel")}
              </SummarySettingsRowLabel>
              <SummarySettingsRowValue>
                {t("Salary.PaymentFileFormatValue")}
              </SummarySettingsRowValue>
            </SummarySettingsRow>
            <SummarySettingsRow>
              <SummarySettingsRowLabel>
                {t("System.CompanyBankAccountType")}
              </SummarySettingsRowLabel>
              <SummarySettingsRowValue>
                {viewModel.bankAccountTypeLabel}
              </SummarySettingsRowValue>
            </SummarySettingsRow>
            <SummarySettingsRow>
              <SummarySettingsRowLabel>
                {t("System.CompanyBankAgreementId")}
              </SummarySettingsRowLabel>
              <SummarySettingsRowValue>
                {viewModel.bankAgreementId}
              </SummarySettingsRowValue>
            </SummarySettingsRow>
          </SummarySettingsRows>
          <SummaryMetaRow>
            <SummaryLabel>{t("Salary.PaymentBankSettingsTitle")}</SummaryLabel>
            <SummarySettingsLink type="button" onClick={handleOpenBankSettings}>
              {t("Salary.PaymentChangeBankSettings")}
            </SummarySettingsLink>
          </SummaryMetaRow>
        </SummaryCard>
      </SummaryGrid>

      {renderState()}

      <InfoCard>
        <InfoTitle>{t("Salary.PaymentFileSummaryTitle")}</InfoTitle>
        <SectionDescription>
          {t("Salary.PaymentFileSummaryFormat")}
        </SectionDescription>
        <SectionDescription>
          {t("Salary.PaymentFileSummaryBankSupport")}
        </SectionDescription>
        <SectionDescription>
          {t("Salary.PaymentFileSummaryImmutable")}
        </SectionDescription>
      </InfoCard>
    </SidePanel>
  );
};
